import { MessageSquare, Bot, Zap } from 'lucide-react';
import type { Assistant } from '../../types/assistant';
import Badge from '../ui/Badge';
import { cn } from '../../lib/utils';

interface AssistantSelectorProps {
  assistants: Assistant[];
  selectedAssistant: Assistant | null;
  onSelectAssistant: (assistant: Assistant) => void;
}

// Assistant type display mapping
const ASSISTANT_TYPE_DISPLAY = {
  generic: 'Genérico',
  mahia: 'MahIA - Super Agente',
  orion: 'Orion - Assistente Pessoal',
  pingy: 'Pingy - Assistente para ISP',
  synergi: 'Synergi - Assistente para ACEs',
} as const;

// Assistant type icons
const ASSISTANT_TYPE_ICONS = {
  generic: Bot,
  mahia: Zap,
  orion: MessageSquare,
  pingy: MessageSquare,
  synergi: MessageSquare,
} as const;

function AssistantSelector({
  assistants,
  selectedAssistant,
  onSelectAssistant,
}: AssistantSelectorProps) {
  const getAssistantTypeDisplay = (type: string) => {
    return ASSISTANT_TYPE_DISPLAY[type as keyof typeof ASSISTANT_TYPE_DISPLAY] || type;
  };

  const getAssistantIcon = (type: string) => {
    return ASSISTANT_TYPE_ICONS[type as keyof typeof ASSISTANT_TYPE_ICONS] || Bot;
  };

  return (
    <div className="space-y-2">
      {assistants.map(assistant => {
        const Icon = getAssistantIcon(assistant.type);
        const isSelected = selectedAssistant?.id === assistant.id;

        return (
          <button
            key={assistant.id}
            onClick={() => onSelectAssistant(assistant)}
            className={cn(
              'w-full text-left p-4 rounded-lg border-2 transition-all duration-200',
              'hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/10',
              isSelected
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            )}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    isSelected
                      ? 'bg-primary-100 dark:bg-primary-900/30'
                      : 'bg-gray-100 dark:bg-gray-700'
                  )}
                >
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      isSelected
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-400'
                    )}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {assistant.name}
                  </h3>
                  {assistant.current_status && (
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full flex-shrink-0',
                        assistant.current_status === 'active' ? 'bg-green-500' : 'bg-red-500'
                      )}
                    />
                  )}
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {getAssistantTypeDisplay(assistant.type)}
                </p>

                <div className="flex items-center justify-between mt-2">
                  <Badge variant="secondary" size="sm">
                    {assistant.signature}
                  </Badge>

                  {assistant.status_mode && (
                    <span className="text-xs text-gray-400">
                      {assistant.status_mode === 'always_active'
                        ? 'Sempre ativo'
                        : assistant.status_mode === 'always_inactive'
                        ? 'Sempre inativo'
                        : 'Automático'}
                    </span>
                  )}
                </div>

                {assistant.summary && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                    {assistant.summary}
                  </p>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default AssistantSelector;
